import React, { Component, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Modal, Spinner } from "react-bootstrap";
import {
  BenchmarkStatus,
  BenchmarkStatusDefaultColors,
  BenchmarkStatusDisplayedNames,
  ColorCriteria,
  DataPeriod,
  EvidenceColumn,
  EvidencePeriodData,
  GradeLevels,
  Measurement,
  SelectEvidenceViewBenchmarkSummary,
} from "../../../../../../store/onboarding/types";
import { ApplicationState } from "../../../../../../store";
import { bindActionCreators, Dispatch } from "redux";
import {
  getCustomColorCriteria,
  hideEditMeasureColorCriteriaModal,
  openEditMeasureColorCriteriaModal,
  restoreDefaultBenchmarks,
  updateCustomColorCriteria,
  setNewCustomColorCriteria,
  getUpdatedAreaOfNeed,
  getViewBenchmarkSummary,
} from "../../../../../../store/onboarding/actions";
import { connect, useSelector } from "react-redux";
import LoadingIndicator from "../../../LoadingIndicator";

import { toastr } from "react-redux-toastr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faTimes } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import Row from "react-bootstrap/Row";
import {
  getTimePeriodSortLabel,
  Grade,
  TimePeriod,
} from "../../../../../../store/groups/types";
import { ValueType } from "react-select/src/types";
import ColorCriteriaTable from "./ColorCriteriaTable";
import { USState } from "../../../../States";
import { useLoading } from "../../../../../../utils/hooks/useLoading";
import BenchmarkFilters from "./BenchmarkFilters";
import useBenchmarkFilters from "./hooks/useBenchmarkFilters";
import useColorCriteria from "./hooks/useColorCriteria";
import { showConfirmDialog } from "../../../../../../store/confirm-dialog/actions";
import { useDispatch } from "react-redux";
import EditMeasureColorCriteriaModal from "../../../../../../containers/color-picker/EditMeasureColorCriteriaModal";
import { UserInfo } from "../../../../../../store/auth/types";

type PropsFromState = {
  showModal: boolean;
  showModalEditMeasureColorCriteriaModal: boolean;
  isLoading: {
    getCustomColorCriteria: boolean;
    restoreDefaultBenchmarks: boolean;
    updateCustomColorCriteria: boolean;
    updateValueOfDSEtoDataPeriod: boolean;
    getViewBenchmarkSummary: boolean;
  };
  errors: {
    getCustomColorCriteria?: string;
    restoreDefaultBenchmarks?: string;
    updateCustomColorCriteria?: string;
    getViewBenchmarkSummary?: string;
  };
  dataPeriod: DataPeriod;
  viewBenchmarkSummary: any;
};

type DispatchProps = {
  openEditMeasureColorCriteriaModal: () => any;
  hideEditMeasureColorCriteriaModal: () => any;
  getCustomColorCriteria: (measurementId: number) => any;
  updateCustomColorCriteria: (
    selectedEvidence: SelectEvidenceViewBenchmarkSummary,
    measurementId: number,
    colorCriteria: Array<ColorCriteria>
  ) => any;
  setNewCustomColorCriteria: (
    selectedEvidence: SelectEvidenceViewBenchmarkSummary,
    measurementId: number,
    colorCriteria: Array<ColorCriteria>,
    user_id: number
  ) => any;
  restoreDefaultBenchmarks: (measurementId: number) => any;
  getUpdatedAreaOfNeed: (dataPeriodId: number, isBehavior: boolean) => any;
};

type OwnProps = {
  onHide: (measurement?: Measurement) => any;
  selectedEvidence: {
    assessmentName: string;
    evidenceColumnGroupId: number;
    evidenceColumn: EvidenceColumn;
    isBehaviorPrimaryScreening: boolean;
    useGradeSpecificColorCriteria: boolean;
    withReadingAlgorithm: boolean;
    isSystemAssessment: boolean;
  };
  lowestGradeInData: string;
};

type Props = PropsFromState & DispatchProps & OwnProps;

const AT_ORDER_KEY = 3; // Constant to ensure "At" benchmark appears in the correct order when added back in

const ViewBenchmarksModal: React.FC<Props> = (props) => {
  const currentUser: UserInfo = useSelector(
    (s: ApplicationState) => s.auth.userInfo!
  );

  const [
      showEditMeasureColorCriteriaModal,
      setShowEditMeasureColorCriteriaModal,
    ] = useState<boolean>(false);

  const [isRemoveDefaultBenchmarks, setRemoveDefaultBenchmarks] = useState(false);

  const gradeOrder = ["K", ...Array.from({ length: 12 }, (_, i) => `G${i + 1}`)];  
  let [searchFilter, setSearchFilter] = useState<any>({
    filterIndex: 0,
    filterGrade: [],
    filterAdditionalBenchmark: true,
    ...gradeOrder.reduce((acc, grade) => {
      acc[`selectFor${grade}`] = [];
      return acc;
    }, {} as any),
  });

  const supportedGrades: Grade[] = useMemo(() => {
    return ["K", "G1", "G2", "G3", "G4", "G5", "G6", "G7", "G8", "G9", "G10", "G11", "G12"];
  }, []);
  const timePeriods = useMemo(() => {
    return Object.values(TimePeriod);
  }, []);
  const benchmarkStatuses = useMemo(() => {
    return Object.values(BenchmarkStatus);
  }, []);

  const raBenchmarkForNumericType = useMemo(() => {
    const colorCriteriaList: ColorCriteria[] = [];
    supportedGrades.forEach((grade) => {
      timePeriods.forEach((timePeriod) => {
        let orderKey = 1;
        benchmarkStatuses.forEach((benchmarkStatus) => {
          const colorCriteria: ColorCriteria = {
            display_name: BenchmarkStatusDisplayedNames[benchmarkStatus],
            color: BenchmarkStatusDefaultColors[benchmarkStatus],
            grade: grade,
            time_period: timePeriod,
            benchmark_status: benchmarkStatus,
            isVisible: true,
            order: orderKey++
          };
          colorCriteriaList.push(colorCriteria);
        });
      });
    });

    return colorCriteriaList;
  }, [supportedGrades, timePeriods, benchmarkStatuses]);
  
  const { isLoading, errors, selectedEvidence, lowestGradeInData } = props;
  let measurement: Measurement = selectedEvidence.evidenceColumn.measurement;
  const isBehavior = selectedEvidence.isBehaviorPrimaryScreening
  let isCloseModel:boolean = false;
  const onSuccess = useCallback(async () => {
    await props.getUpdatedAreaOfNeed(props.dataPeriod.id, isBehavior);
    props.hideEditMeasureColorCriteriaModal();
  }, [selectedEvidence, props]);
  useLoading(
    isLoading.updateCustomColorCriteria,
    errors.updateCustomColorCriteria,
    onSuccess
  );
  
  useEffect(() => {
   if(props.showModal) {
     if(selectedEvidence) {
      dispatch(getViewBenchmarkSummary(selectedEvidence.evidenceColumn.id));
     }
   }
 },[props.showModal])

  const onRestoreDefaultBenchmarksSuccess = useCallback(() => {
    getCustomColorCriteria();
  }, []);

  useLoading(
    isLoading.restoreDefaultBenchmarks,
    errors.restoreDefaultBenchmarks,
    onRestoreDefaultBenchmarksSuccess
  );

  const {
    customColorCriteria,
  } = useColorCriteria({
    selectedGrade: lowestGradeInData as Grade,
    selectedTimePeriod: props.dataPeriod.time_period as TimePeriod,
    measurement,
  });

  const updatedCustomColorCriteria = useMemo(() => {
    const colorCriteriaArr: ColorCriteria[] = [
      ...(customColorCriteria || []),
    ];

    if (!selectedEvidence.useGradeSpecificColorCriteria) {
      return colorCriteriaArr.map(
        (colorCriteria: ColorCriteria) => ({
          ...colorCriteria,
          isVisible: colorCriteria.isVisible,
        })
      );
    }

    /**
     * Normalize custom criteria
     * Add missing order from RA benchmark data
     */
    const normalizedColorCriteriaArr = colorCriteriaArr.map(
      (item) => {
        if (item.order !== undefined) {
          return item;
        }

        const matchedRAItem =
          raBenchmarkForNumericType.find(
            (raItem) =>
              raItem.grade === item.grade &&
              raItem.time_period === item.time_period &&
              raItem.display_name === item.display_name
          );

        return {
          ...item,
          order: matchedRAItem?.order,
        };
      }
    );

    /**
     * Merge custom criteria with RA benchmark defaults
     */
    const mergedCustomColorCriteria =
      raBenchmarkForNumericType.map((raItem) => {
        const customItem =
          normalizedColorCriteriaArr.find(
            (c) =>
              c.grade === raItem.grade &&
              c.time_period ===
                raItem.time_period &&
              c.order === raItem.order
          );

        return customItem
          ? { ...raItem, ...customItem }
          : raItem;
      });

    /**
     * Selected grade/time periods
     */
    const mergedTimePeriod = [
      ...[
        "K",
        ...Array.from(
          { length: 12 },
          (_, i) => `G${i + 1}`
        ),
      ].flatMap(
        (grade) =>
          searchFilter[`selectFor${grade}`] || []
      ),
    ];

    /**
     * Apply visibility logic
     */
    return mergedCustomColorCriteria.map(
      (colorCriteria: ColorCriteria) => {
        const isVisibleCheck =
          mergedTimePeriod.some(
            (timePeriod: any) =>
              timePeriod.grade ===
                colorCriteria.grade &&
              timePeriod.value ===
                colorCriteria.time_period &&
              colorCriteria.isVisible
          );

        return {
          ...colorCriteria,

          // Preserve existing values
          range_min: colorCriteria.range_min,
          range_max: colorCriteria.range_max,

          // Visibility toggle only
          isVisible:
            colorCriteriaArr.length > 0
              ? isVisibleCheck
              : true,
        };
      }
    );
  }, [
    customColorCriteria,
    searchFilter,
    selectedEvidence.useGradeSpecificColorCriteria,
  ]);


  const {
    filters,
    selectedGrade,
    setSelectedGrade,
    selectedTimePeriod,
    setSelectedTimePeriod,
    filterDisabled,
    setFilterDisabled
  } = useBenchmarkFilters({
    show:
      !!measurement.grade_specific_color_criteria ||
      !!measurement.custom_color_criteria?.length || 
      !!customColorCriteria,
    evidenceColumnGroupId: selectedEvidence.evidenceColumnGroupId,
    isCloseModel,
  });

  const getCustomColorCriteria = () => {
    const { measurement } = selectedEvidence.evidenceColumn;
    if (measurement.id) {
      props.getCustomColorCriteria(measurement.id);
    }
  };

  //Old code
  // const handleApplyColorCriteriaChanges = (measurement: Measurement) => {
  //   if (measurement.id && measurement.color_criteria) {
  //     props.updateCustomColorCriteria(
  //       measurement.id,
  //       measurement.color_criteria
  //     );
  //   }
  // };

   function fixBenchmarkStatus(data: any[]) {
    const requiredStatuses = [
      BenchmarkStatus.WellBelow,
      BenchmarkStatus.Below,
      BenchmarkStatus.At,
      BenchmarkStatus.AtOrAbove
    ];
  
    // Find existing statuses
    const existingStatuses = data.map(d => d.benchmark_status);
  
    // Find missing status
    const missingStatus = requiredStatuses.find(
      status => !existingStatuses.includes(status)
    );
  
    if (!missingStatus) return data;
  
    // Find duplicate status
    const counts: Record<string, number> = {};
    existingStatuses.forEach(s => {
      counts[s] = (counts[s] || 0) + 1;
    });
  
    const duplicateStatus = Object.keys(counts).find(key => counts[key] > 1);
  
    if (!duplicateStatus) return data;
  
    // Get duplicate rows
    const duplicates = data
      .filter(d => d.benchmark_status === duplicateStatus)
      .sort((a, b) => a.range_min - b.range_min);
  
    // Smallest range_min gets missing status
    const targetRangeMin = duplicates[0].range_min;
  
    // Replace duplicate with missing status
    const fixedData = data.map(item => {
      if (
        item.benchmark_status === duplicateStatus &&
        item.range_min === targetRangeMin
      ) {
        return {
          ...item,
          benchmark_status: missingStatus
        };
      }
      return item;
    });
  
    return fixedData;
  }

  const handleApplyColorCriteriaChanges = (
    measurement: Measurement,
    searchFilter: any
  ) => {
    setShowEditMeasureColorCriteriaModal(false);
    if(measurement) {
      measurement = {
        ...measurement,
        isAddNew: false,
      }
    }
    
    if (selectedEvidence.useGradeSpecificColorCriteria) {
      let mergedTimePeriod = [
        ...["K", ...Array.from({ length: 12 }, (_, i) => `G${i + 1}`)]
        .flatMap((grade) => searchFilter[`selectFor${grade}`] || [])
      ];

      if (measurement.id && measurement.color_criteria) {
        
        const updatedColorCriteria = measurement.color_criteria.map(
            (colorCriteria: ColorCriteria) => {
              // ✅ time/grade match
              const timeMatch = mergedTimePeriod.some(
                (tp: any) =>
                  tp.grade === colorCriteria.grade &&
                  tp.value === colorCriteria.time_period
              );

              // ✅ benchmark rule (FINAL CORRECT LOGIC)
              let benchmarkMatch = true;

              if (!searchFilter.filterAdditionalBenchmark) {
                benchmarkMatch =
                  colorCriteria.order !== AT_ORDER_KEY;
              }

              // if 4 → allow all (already true)

              // ✅ FINAL VISIBILITY (fresh calculation)
              const isVisibleCheck = timeMatch && benchmarkMatch;

              return {
                ...colorCriteria,
                range_min: isVisibleCheck ? colorCriteria.range_min : undefined,
                range_max: isVisibleCheck ? colorCriteria.range_max : undefined,
                isVisible: isVisibleCheck
              };
            }
          );
        props.updateCustomColorCriteria(
          selectedEvidence,
          measurement.id,
          updatedColorCriteria.filter(
            (cc) =>
              cc.isVisible &&
              cc.range_min !== undefined &&
              cc.range_max !== undefined
          )
        ).then((res: any) => {
          if(isRemoveDefaultBenchmarks) {
            setRemoveDefaultBenchmarks(false);
          }
        });
      }

      setSearchFilter((prev: any) => ({
        ...prev,
        ...searchFilter
      }));
    } else {
      let colorCriteriaArr: ColorCriteria[] = 
        measurement.color_criteria?.filter(
          (cc) =>
            cc.isVisible &&
            cc.range_min !== undefined &&
            cc.range_max !== undefined
        ) || [];
      
      props.updateCustomColorCriteria(
        selectedEvidence,
        measurement.id!,
        fixBenchmarkStatus(colorCriteriaArr)
      ).then((res: any) => {
        if(isRemoveDefaultBenchmarks) {
          setRemoveDefaultBenchmarks(false);
        }
      });
    }
  };

  const handleApplyNewDistrictBenchmarkCriteriaChanges = (
    measurement: Measurement, searchFilter: any
  ) => {
    if(searchFilter.filterGrade && searchFilter.filterGrade.length > 0) {
       setSearchFilter((prev: any) => {
          if (prev !== undefined) {
            return prev;
          }
  
          return {
            ...prev,
            ...searchFilter,
          };
        });
    }
    if (measurement.id && measurement.color_criteria) {
      props.setNewCustomColorCriteria(
        selectedEvidence,
        measurement.id,
        fixBenchmarkStatus(measurement.color_criteria?.filter((cc) => cc.isVisible && cc.range_min !== undefined && cc.range_max !== undefined) || []),
        currentUser.id
      );
    }
  };

  const {
    mainColorCriteria,
    allColorCriteria,
  } = useColorCriteria({
    selectedGrade,
    selectedTimePeriod,
    measurement,
  });

  const benchmarkLabel = useMemo(() => {
    return customColorCriteria.length && updatedCustomColorCriteria.length
        ? "Edit"
        : mainColorCriteria.length
          ? "Set"
          : "Create"
    }, [customColorCriteria, updatedCustomColorCriteria, mainColorCriteria]);

  /*const getGrade = (grades: Array<string>) => {
    let lowestGradeIndex = grades.indexOf(lowestGradeInData)
        if(lowestGradeIndex!=-1) return grades[lowestGradeIndex]
        else return grades[0] as Grade;
  }*/

  const handleModalOpen = () => {
    let grade: any;
    let timePeriod: any;

    const measurement: Measurement =
      selectedEvidence.evidenceColumn.measurement;
    let grades: Array<string> = [];

    /*if (measurement.grade_specific_color_criteria) {

      grades = Object.keys(measurement.grade_specific_color_criteria);
    }

    if (grades.length && measurement.grade_specific_color_criteria) {
      grade = getGrade(grades);
      const criteria = measurement.grade_specific_color_criteria!;
      timePeriod = props.dataPeriod.time_period;
      if (!criteria[grade][timePeriod]) {
        const keys = Object.keys(criteria[grade]);
        if (keys.length > 0) {
          timePeriod = keys[0] as TimePeriod;
        }
      }
    }

    if (measurement.custom_color_criteria) {
      measurement.custom_color_criteria.forEach(ccc=>{
        if(ccc.grade) grades.push(ccc.grade);
      })
    }

    if (grades.length && measurement.custom_color_criteria && measurement.custom_color_criteria.length>0) {
      grade = getGrade(grades);
      const criteria = measurement.custom_color_criteria!;
      timePeriod = props.dataPeriod.time_period;
      if (!timePeriod || criteria.findIndex(x=>x.grade==grade && x.time_period==timePeriod)==-1) {
        timePeriod = criteria[criteria.findIndex(x=>x.grade==grade)].time_period
      }*/

    grade = lowestGradeInData;
    timePeriod = props.dataPeriod.time_period;
    isCloseModel = false;
    setSelectedGrade(grade);
    setSelectedTimePeriod(timePeriod);
    getCustomColorCriteria();
  };

  const handleModalHide = () => {
    const {
      getCustomColorCriteria,
      updateCustomColorCriteria,
      restoreDefaultBenchmarks,
    } = isLoading;
    if (
      !getCustomColorCriteria &&
      !updateCustomColorCriteria &&
      !restoreDefaultBenchmarks
    ) {
      if (customColorCriteria.length) {
        const measurement: Measurement = {
          ...selectedEvidence.evidenceColumn.measurement,
          custom_color_criteria: customColorCriteria,
        };
        props.onHide(measurement);
      } else {
        props.onHide();
      }

      setFilterDisabled(false);
    }
  };

  const handleDefaultBenchmarksRestore = () => {
    const { measurement } = selectedEvidence.evidenceColumn;
    if (measurement.id) {
      props.restoreDefaultBenchmarks(measurement.id);
    }
  };

  const dispatch = useDispatch();

  const handleShowEditMeasureColorCriteriaModal = () => {
    const colorCriteriaArr: ColorCriteria[] = [
      ...(customColorCriteria || []),
    ];

    const gradeOrder = [
      "K",
      ...Array.from({ length: 12 }, (_, i) => `G${i + 1}`),
    ];

    if (colorCriteriaArr.length > 0 && props.showModal) {
      setSearchFilter(() => {
        const uniqueGrades = Array.from(
          new Set(colorCriteriaArr.map((cc) => cc.grade))
        );

        // Determine whether any grade/time-period has 4+ benchmark levels
        const additionalBenchmark = uniqueGrades.some((grade) => {
          const criteriaForGrade = colorCriteriaArr.filter(
            (cc) => cc.grade === grade
          );

          const groupedByTimePeriod = criteriaForGrade.reduce(
            (acc, item) => {
              if (!acc[item.time_period!]) {
                acc[item.time_period!] = [];
              }

              acc[item.time_period!].push(item);

              return acc;
            },
            {} as Record<string, ColorCriteria[]>
          );

          return Object.values(groupedByTimePeriod).some(
            (criteria) => criteria.length > 3
          );
        });

        const selectForGrades = gradeOrder.reduce((acc, grade) => {
          const uniqueTimePeriods = Array.from(
            new Set(
              colorCriteriaArr
                .filter((cc) => cc.grade === grade)
                .map((cc) => cc.time_period)
            )
          );

          acc[`selectFor${grade}`] = uniqueTimePeriods.map(
            (timePeriod) => ({
              value: timePeriod,
              label: getTimePeriodSortLabel(timePeriod!),
              grade,
            })
          );

          return acc;
        }, {} as any);

        return {
          filterIndex: 0,

          filterGrade: uniqueGrades.map((grade) => ({
            value: grade,
            label: grade,
          })),

          // TRUE => 4 benchmark levels
          filterAdditionalBenchmark: additionalBenchmark,

          ...selectForGrades,
        };
      });
    } else {
      setSearchFilter({
        filterIndex: 0,
        filterGrade: [],
        filterAdditionalBenchmark: true,
        ...gradeOrder.reduce((acc, grade) => {
          acc[`selectFor${grade}`] = [];
          return acc;
        }, {} as any),
      });
    }
    props.openEditMeasureColorCriteriaModal();
  };

  const handleShow = (id: any) => {
    if(benchmarkLabel.includes("Edit") || benchmarkLabel.includes("Set")) {
      dispatch(
        showConfirmDialog({
          title: `${benchmarkLabel} District Benchmarks`,
          text: (
            <>
              <p className="text-center font-weight-bold">Are you sure you want to continue?</p>
              {benchmarkLabel === "Set" ? (
                <p className="text-center">Setting district benchmarks for some grades and time periods will remove all benchmarks from other grades and time periods.</p>
              ): (
                <>
                  <p className="text-center">District benchmarks apply to all district users and may affect grouping decisions made using previous benchmarks.</p>
                  <p className="text-center">You’ll be able to review and edit the benchmarks before applying them.</p>
                </>
              )}
              <p className="text-center">
                <FontAwesomeIcon 
                  icon={faExclamationTriangle}
                  style={{ color: 'red' }}
                /> {" "}
                <i>This action cannot be undone.</i>
              </p>
            </>
          ),
          onConfirm: () => {
            handleShowEditMeasureColorCriteriaModal();
          },
          confirmButtonText: benchmarkLabel,
        })
      );
    } else {
      handleShowEditMeasureColorCriteriaModal();
    }
  };
  
  const evidenceColumnStdScore = useMemo(() => {
    if(selectedGrade) {
      return props.viewBenchmarkSummary?.[selectedGrade!]
    } else {
      let gradeArray: any[] = [];
      if(props.viewBenchmarkSummary) {
        for (const key in props.viewBenchmarkSummary) {
          if (Object.prototype.hasOwnProperty.call(props.viewBenchmarkSummary, key)) {
            const element = props.viewBenchmarkSummary[key];
            if(Array.isArray(element)) {
              gradeArray = [
                ...gradeArray,
                ...element
              ]
            }
          }
        }
      }
      return gradeArray;
    }
  },[props.viewBenchmarkSummary,selectedGrade])

  return (
    <>
      {
        <Modal
          animation={false}
          backdropClassName="customDarkModalBackdrop inner in"
          show={props.showModal}
          centered
          onShow={handleModalOpen}
          onHide={handleModalHide}
        >
          <Modal.Header className="purpleModalHeader" closeButton>
            <Modal.Title>Benchmark Summary</Modal.Title>
          </Modal.Header>
          <Modal.Body className="viewBenchmarksModal">
            <h4 className="mb-1">
              <strong>Assessment Name:</strong> {selectedEvidence.assessmentName}
            </h4>
            <h4 className="mb-3">
              <strong>Measurement:</strong> {measurement.display_name}
            </h4>
            <p className="mb-1">
              This table shows the number and percentage of students at each benchmark level for the selected measure in this assessment.
            </p>
            <div className="my-3">
              <span className="alert alert-warning p-1 " style={{"fontSize": "0.95em"}}>
                <strong>Note:</strong> Counts reflect any filters applied to the data table.
              </span>
            </div>
            {isLoading.getCustomColorCriteria || isLoading.getViewBenchmarkSummary ? (
              <LoadingIndicator />
            ) : (
              <>
                  {filters}

                  <ColorCriteriaTable
                    allColorCriteria={allColorCriteria}
                    //measurementType={measurement.type}
                    measurementType={props.viewBenchmarkSummary?.measurement_type}
                    selectedGrade={selectedGrade}
                    evidenceColumnStdScore={evidenceColumnStdScore}
                  />
                <div className="d-flex justify-content-around flex-wrap">
                  <Button
                    bsPrefix="b"
                    className="blueBtnSm text-nowrap mt-3"
                    //onClick={props.openEditMeasureColorCriteriaModal}
                    onClick={handleShow}
                  >
                    {customColorCriteria.length && updatedCustomColorCriteria.length > 0
                      ? "Edit District Benchmarks"
                      : mainColorCriteria.length
                      ? "Set District Benchmarks"
                      : "Create District Benchmarks"}
                  </Button>
                  {/* {customColorCriteria.length > 0 && (
                    <Button
                      bsPrefix="b"
                      className="blueBtnSm text-nowrap mt-3"
                      onClick={handleDefaultBenchmarksRestore}
                    >
                      USE DEFAULT BENCHMARKS{" "}
                      {isLoading.restoreDefaultBenchmarks && (
                        <Spinner animation="border" size="sm" />
                      )}
                    </Button>
                  )} */}
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
      }

      {props.showModalEditMeasureColorCriteriaModal && searchFilter
      ? <EditMeasureColorCriteriaModal
          withGrades
          fromViewBenchmarks
          selectedGrade={selectedGrade}
          selectedTimePeriod={selectedTimePeriod}
          measurement={{
            ...measurement,
            ...((mainColorCriteria.length > 0) &&
              (!customColorCriteria.length && !updatedCustomColorCriteria.length)
              ? { isAddNew: true } : { isAddNew: false }),
            color_criteria: updatedCustomColorCriteria,
          }}
          onApplyChanges={customColorCriteria.length > 0
            ? handleApplyColorCriteriaChanges
            : handleApplyNewDistrictBenchmarkCriteriaChanges}
          isLoading={isLoading.updateValueOfDSEtoDataPeriod ?? false}
          evidenceColumnGroupId={selectedEvidence.evidenceColumnGroupId} 
          filterDisabled={filterDisabled} 
          setFilterDisabled={setFilterDisabled }  
          useGradeSpecificColor={selectedEvidence.useGradeSpecificColorCriteria}
          withReadingAlgorithm={selectedEvidence.withReadingAlgorithm}
          isSystemAssessment={selectedEvidence.isSystemAssessment}
          searchFilter={searchFilter}
          onHandleSearchFilter={
            (updatedSearchFilter: any) => 
              setSearchFilter((prev: any) => ({ 
                ...prev, ...updatedSearchFilter 
              }))
          }
          benchmarkLabel={benchmarkLabel}
          isRemoveDefaultBenchmarks={isRemoveDefaultBenchmarks}
          onRemoveDefaultBenchmarks = {setRemoveDefaultBenchmarks}
          // withGrades={useGradeSpecificColor}
          // useGradeSpecificColor={useGradeSpecificColor}
          // withReadingAlgorithm = {withReadingAlgorithm}
          // onApplyChanges={handleApplyColorCriteriaChanges}
          // measurement={selectedMeasurement}
        />
      : null}
      

      {/* <EditMeasureColorCriteriaModal
        withGrades
        fromViewBenchmarks
        selectedGrade={selectedGrade}
        selectedTimePeriod={selectedTimePeriod}
        measurement={{
          ...measurement,
          color_criteria: customColorCriteria,
        }}
        onApplyChanges={customColorCriteria.length > 0
          ? handleApplyColorCriteriaChanges
          : handleApplyNewDistrictBenchmarkCriteriaChanges}
        isLoading={isLoading.updateCustomColorCriteria}
        evidenceColumnGroupId={selectedEvidence.evidenceColumnGroupId} 
        filterDisabled={filterDisabled} 
        setFilterDisabled={setFilterDisabled }  
        useGradeSpecificColor={selectedEvidence.useGradeSpecificColorCriteria}
        withReadingAlgorithm={selectedEvidence.withReadingAlgorithm}
        isSystemAssessment={selectedEvidence.isSystemAssessment}
      /> */}
    </>
  );
};

const mapStateToProps = ({ onboarding }: ApplicationState): PropsFromState => {
  return {
    showModal: onboarding.modalsState.viewBenchmarksModal,
    showModalEditMeasureColorCriteriaModal: onboarding.modalsState.editMeasureColorCriteriaModal,
    dataPeriod: onboarding.currentDataPeriod!,
    viewBenchmarkSummary: onboarding.viewBenchmarkSummary,
    isLoading: {
      getCustomColorCriteria: onboarding.isLoading.getCustomColorCriteria,
      updateCustomColorCriteria: onboarding.isLoading.updateCustomColorCriteria,
      updateValueOfDSEtoDataPeriod: onboarding.isLoading.updateValueOfDSEtoDataPeriod,
      restoreDefaultBenchmarks: onboarding.isLoading.restoreDefaultBenchmarks,
      getViewBenchmarkSummary: onboarding.isLoading.getViewBenchmarkSummary
    },
    errors: {
      getCustomColorCriteria: onboarding.errors.getCustomColorCriteria,
      updateCustomColorCriteria: onboarding.errors.updateCustomColorCriteria,
      restoreDefaultBenchmarks: onboarding.errors.restoreDefaultBenchmarks,
      getViewBenchmarkSummary: onboarding.errors.getViewBenchmarkSummary
    },
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps =>
  bindActionCreators(
    {
      openEditMeasureColorCriteriaModal: openEditMeasureColorCriteriaModal,
      hideEditMeasureColorCriteriaModal: hideEditMeasureColorCriteriaModal,
      getCustomColorCriteria: getCustomColorCriteria,
      updateCustomColorCriteria: updateCustomColorCriteria,
      setNewCustomColorCriteria: setNewCustomColorCriteria,
      restoreDefaultBenchmarks: restoreDefaultBenchmarks,
      getUpdatedAreaOfNeed: getUpdatedAreaOfNeed,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ViewBenchmarksModal);
